import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { History, RotateCcw, Pin, PinOff } from 'lucide-react';
import { toast } from 'sonner';

const Workflow = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const [saveOpen, setSaveOpen] = useState(false);
  const [savingItem, setSavingItem] = useState(null);
  const [saveName, setSaveName] = useState('');
  const [saveNotes, setSaveNotes] = useState('');

  const recent = useMemo(() => items.filter((x) => !x.is_saved).slice(0, 20), [items]);
  const saved = useMemo(() => items.filter((x) => x.is_saved), [items]);

  const fetchHistory = async () => {
    try {
      const res = await axios.get(`${API}/calculations/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load recent calculations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openSave = (item) => {
    setSavingItem(item);
    setSaveName(item.name || `${item.material} · ${item.tool_diameter}mm · ${item.flute_count}F`);
    setSaveNotes(item.notes || '');
    setSaveOpen(true);
  };

  const handleSave = async () => {
    if (!savingItem) return;
    try {
      await axios.post(
        `${API}/calculations/history/${savingItem.id}/save`,
        { name: saveName, notes: saveNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Saved setup');
      setSaveOpen(false);
      setSavingItem(null);
      await fetchHistory();
    } catch (e) {
      toast.error('Failed to save setup');
    }
  };

  const handleUnsave = async (item) => {
    try {
      await axios.post(`${API}/calculations/history/${item.id}/unsave`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Unpinned');
      await fetchHistory();
    } catch (e) {
      toast.error('Failed to unpin');
    }
  };

  const handleReload = async (item) => {
    try {
      localStorage.setItem('cnc:last_calculation', JSON.stringify({
        formData: {
          material: item.material,
          tool_diameter: item.unit_system === 'inch' ? item.tool_diameter / 25.4 : item.tool_diameter,
          flute_count: item.flute_count,
          spindle_rpm: item.spindle_rpm_input || '',
          tool_type: item.tool_type,
          bit_direction: item.bit_direction,
          spindle_type: item.spindle_type,
        },
        unitSystem: item.unit_system,
      }));

      // Also update Active Job Context
      localStorage.setItem('cnc:active_job_context', JSON.stringify({
        profile: 'Default',
        units: item.unit_system,
        material: item.material,
        tool_diameter: item.tool_diameter,
        flute_count: item.flute_count,
        bit_direction: item.bit_direction,
        safety: 'safe',
        next: 'Review the calculator outputs and run a quick material surface check.'
      }));
      toast.success('Loaded into Calculator');
      window.location.assign('/calculator');
    } catch (e) {
      toast.error('Failed to reload');
    }
  };

  if (!token) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="panel">
          <div className="panel-header">
            <div>
              <div className="panel-title">Job Setup</div>
              <h1 className="text-2xl md:text-3xl font-semibold mt-2">Please log in</h1>
              <p className="text-sm md:text-lg text-[var(--text-secondary)] mt-2">Sign in to see your recent calculations.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="workflow-page" className="max-w-6xl mx-auto space-y-6">
      <div className="panel-header">
        <div>
          <div className="panel-title">Job Setup</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-3">Recent Jobs</h1>
          <p className="text-sm md:text-lg text-[var(--text-secondary)] mt-2">
            A private history of your latest CNC calculations (not a job file system).
          </p>
          <p className="text-sm text-[var(--text-muted)] mt-1">Signed in as {user?.full_name}</p>
        </div>
      </div>

      <div className="divider"></div>

      {loading ? (
        <div className="text-[var(--text-secondary)]">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
            <CardHeader>
              <CardTitle className="text-[#292524] flex items-center gap-2">
                <History className="w-5 h-5 text-[var(--accent-primary)]" />
                Recent Jobs
              </CardTitle>
              <CardDescription className="text-[#78716C]">Auto-saved (last 20)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {recent.length === 0 ? (
                <div className="text-[#78716C]">No calculations yet. Run the calculator to build history.</div>
              ) : (
                recent.map((x) => (
                  <div key={x.id} className="p-3 border border-[#D6D3D1] bg-white/60">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-[#292524] truncate">
                          {x.material} · {x.tool_diameter}mm · {x.flute_count}F
                        </div>
                        <div className="text-xs text-[#78716C] mt-1">
                          RPM {Math.round(x.spindle_rpm)} · Feed {x.feed_rate} · DOC {x.pass_depth} · {new Date(x.created_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleReload(x)} className="h-8 px-2 text-xs">
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reload
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openSave(x)} className="h-8 px-2 text-xs">
                          <Pin className="w-4 h-4 mr-1" />
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card className="bg-white/80 backdrop-blur-sm border-[#D6D3D1]">
            <CardHeader>
              <CardTitle className="text-[#292524] flex items-center gap-2">
                <Pin className="w-5 h-5 text-[var(--accent-primary)]" />
                Saved Setups
              </CardTitle>
              <CardDescription className="text-[#78716C]">Pinned, named setups + notes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {saved.length === 0 ? (
                <div className="text-[#78716C]">No saved setups yet. Pin one from your Recent list.</div>
              ) : (
                saved.map((x) => (
                  <div key={x.id} className="p-3 border border-[#D6D3D1] bg-white/60">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-[#292524] truncate">{x.name || 'Saved setup'}</div>
                        <div className="text-xs text-[#78716C] mt-1">
                          {x.material} · {x.tool_diameter}mm · {x.flute_count}F · RPM {Math.round(x.spindle_rpm)} · Feed {x.feed_rate}
                        </div>
                        {x.notes && <div className="text-sm text-[#57534E] mt-2 whitespace-pre-wrap">{x.notes}</div>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleReload(x)} className="h-8 px-2 text-xs">
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Reload
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openSave(x)} className="h-8 px-2 text-xs">
                          Edit
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleUnsave(x)} className="h-8 px-2 text-xs">
                          <PinOff className="w-4 h-4 mr-1" />
                          Unpin
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-white/90 backdrop-blur-sm border-[#D6D3D1]">
          <DialogHeader>
            <DialogTitle className="text-[#292524]">Save setup</DialogTitle>
            <DialogDescription className="text-[#78716C]">Give this setup a name and optional notes.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#78716C]">Name</label>
              <Input value={saveName} onChange={(e) => setSaveName(e.target.value)} className="bg-white/60 border-[#D6D3D1] text-[#292524]" />
            </div>
            <div>
              <label className="text-xs text-[#78716C]">Notes (optional)</label>
              <textarea value={saveNotes} onChange={(e) => setSaveNotes(e.target.value)} className="w-full h-28 p-2 bg-white/60 border border-[#D6D3D1] text-[#292524]" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSaveOpen(false)} className="border-[#D6D3D1]">Cancel</Button>
              <Button onClick={handleSave} className="bg-[#F59E0B] hover:bg-[#F59E0B]/90 text-[#0F172A] font-bold">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Workflow;
